import { BlockNoteSchema, defaultBlockSpecs, filterSuggestionItems } from "@blocknote/core";
import "@blocknote/core/fonts/inter.css";
import { BlockNoteView } from "@blocknote/mantine";
import "@blocknote/mantine/style.css";
import {
  BlockNoteViewRaw,
  FormattingToolbar,
  FormattingToolbarController,
  SuggestionMenuController,
  getDefaultReactSlashMenuItems,
  useCreateBlockNote,
} from "@blocknote/react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTheme } from "../Context/ThemeContext";

import { en } from "@blocknote/core/locales";
import { en as aiEn } from "@blocknote/xl-ai/locales";
import "@blocknote/xl-ai/style.css";

import { createOpenAI } from "@ai-sdk/openai";
import {
  AIMenuController,
  AIToolbarButton,
  createAIExtension,
  createBlockNoteAIClient,
  getAISlashMenuItems,
} from "@blocknote/xl-ai";

interface LBMCEditorProps {
  onBlur?: (markdown: string) => void;
  initialMarkDown?: string;
  value?: string;
  className?: string;
  editorId: string;
  readOnly?: boolean;
  accessToken: string | null;
}

const {
  codeBlock,
  image,
  video,
  audio,
  file,
  heading,
  ...remainingBlockSpecs
} = defaultBlockSpecs;

const FormattingToolbarWithAI: React.FC = () => (
  <FormattingToolbarController
    formattingToolbar={() => (
      <FormattingToolbar>
        <AIToolbarButton />
      </FormattingToolbar>
    )}
  />
);

const SuggestionMenuWithAI: React.FC<{ editor: any }> = ({ editor }) => (
  <SuggestionMenuController
    triggerCharacter="/"
    getItems={async (query: any) =>
      filterSuggestionItems(
        [
          ...getDefaultReactSlashMenuItems(editor),
          ...getAISlashMenuItems(editor),
        ],
        query
      )
    }
  />
);

const LBMCEditor: React.FC<LBMCEditorProps> = ({
  initialMarkDown = "",
  onBlur,
  value,
  editorId,
  readOnly = false,
  accessToken,
}) => {
  const { isDarkMode } = useTheme();
  const schema = useMemo(
    () =>
      BlockNoteSchema.create({
        blockSpecs: {
          ...remainingBlockSpecs,
          paragraph: defaultBlockSpecs.paragraph,
        },
      }),
    []
  );

  // AI environment variables (same names as your other example)
  const env = {
    API_KEY: process.env.REACT_APP_AI_API_KEY || "",
    AI_BASE_URL: process.env.REACT_APP_AI_BASE_URL || "",
    AZURE_RESOURCE_NAME: process.env.REACT_APP_AZURE_RESOURCE_NAME || "",
    AZURE_MODEL_NAME: process.env.REACT_APP_AZURE_MODEL_NAME || "",
  };

  // Memoize the AI client and Azure provider to avoid recreating on each render
  const aiClient = useMemo(
    () =>
      createBlockNoteAIClient({
        apiKey: `${accessToken}`,
        baseURL: env.AI_BASE_URL,
      }),
    []
  );

  const openai = createOpenAI({
    ...aiClient.getProviderSettings("openai"),
  });

  const model = useMemo(() => openai(env.AZURE_MODEL_NAME), [openai]);

  // Create the editor including AI extension and locale dictionary
  const editor = useCreateBlockNote({
    schema,
    dictionary: {
      ...en,
      ai: aiEn,
    },
    extensions: [
      createAIExtension({
        model,
      }),
    ],
  });

  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const lastLoadedMarkdown = useRef<string | null>(null);
  const lastSavedMarkdown = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [editorKey, setEditorKey] = useState(0);

  useEffect(() => { }, [editorId]);

  // Prevent drag/drop leaking to other editors
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const stopEvent = (e: DragEvent) => e.stopPropagation();

    container.addEventListener("dragstart", stopEvent);
    container.addEventListener("drop", stopEvent);

    return () => {
      container.removeEventListener("dragstart", stopEvent);
      container.removeEventListener("drop", stopEvent);
    };
  }, []);

  useEffect(() => {
    const handleScroll = (event: Event) => {
      // Check if the scroll event occurred inside the editor container
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setEditorKey((prev) => prev + 1);
      }
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, []);

  useEffect(() => {
    const markdownToLoad = value ?? initialMarkDown;
    if (lastLoadedMarkdown.current !== markdownToLoad) {
      (async () => {
        const currentMarkdown = await editor.blocksToMarkdownLossy(
          editor.document
        );
        if (markdownToLoad !== currentMarkdown) {
          const blocks = await editor.tryParseMarkdownToBlocks(markdownToLoad);
          editor.replaceBlocks(editor.document, blocks);
        }
        lastLoadedMarkdown.current = markdownToLoad;
        lastSavedMarkdown.current = markdownToLoad;
      })();
    }
  }, [editor, value, initialMarkDown]);

  const handleSave = useCallback(async () => {
    if (onBlur) {
      const markdown = await editor.blocksToMarkdownLossy(editor.document);
      if (markdown !== lastSavedMarkdown.current) {
        onBlur(markdown);
        lastSavedMarkdown.current = markdown;
      }
    }
  }, [editor, onBlur]);

  const debouncedSave = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      handleSave();
    }, 2000);
  }, [handleSave]);

  const handleBlur = useCallback(
    async (e: React.FocusEvent) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      await handleSave();
    },
    [handleSave]
  );

  return (
    <div ref={containerRef}>
      {readOnly ? (
        <BlockNoteViewRaw
          editor={editor}
          editable={false}
          formattingToolbar={false}
        />
      ) : (
        <BlockNoteView
          editor={editor}
          className="p-inputtext p-component"
          onBlur={handleBlur}
          onChange={debouncedSave}
          theme={isDarkMode ? "dark" : "light"}
          key={editorKey}
        >
          <AIMenuController />
          <FormattingToolbarWithAI />
          <SuggestionMenuWithAI editor={editor} />
        </BlockNoteView>
      )}
    </div>
  );
};

export default LBMCEditor;